/**
 * Shared iframe helpers used by `deposit` and `connect` surfaces.
 *
 * Two presentations: inline (iframe inside a partner-provided
 * container, current behavior) and overlay (SDK builds a fixed
 * backdrop + centered modal, full-bleed on narrow viewports). The
 * partner picks by passing `container` or omitting it.
 */

/**
 * Permissions the surface needs from the embedding page.
 *
 * `payment` is the deposit approval. `identity-credentials-get` is
 * "Sign in with Google": Chrome routes that button through FedCM, and
 * a cross-origin iframe without this delegation gets the credential
 * request blocked rather than a popup. Neither grants the surface
 * anything the consumer doesn't act on — both are gated behind a
 * click inside our own origin.
 *
 * Note that the iframe is deliberately NOT sandboxed. Adding `sandbox`
 * without `allow-popups` would break the Google popup, and the
 * isolation it would buy is already provided by the origin boundary.
 */
const IFRAME_ALLOW = 'payment; identity-credentials-get';

export interface IframeOptions {
  readonly url: string;
  readonly container: HTMLElement;
  readonly height?: string;
  readonly width?: string;
  readonly title: string;
}

/** Create and append the iframe; return it. Used for inline mounts. */
export function createIframe(opts: IframeOptions): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.src = opts.url;
  iframe.title = opts.title;
  iframe.allow = IFRAME_ALLOW;
  iframe.style.border = '0';
  iframe.style.width = opts.width ?? '100%';
  iframe.style.height = opts.height ?? '640px';
  iframe.style.display = 'block';
  iframe.setAttribute('role', 'dialog');
  opts.container.appendChild(iframe);
  return iframe;
}

/**
 * Which palette the modal renders in — both the overlay chrome and the checkout
 * inside it, which are kept in step (see `themeParam` in `open.ts`).
 *
 * `light` is the default. `auto` follows the VIEWER's OS preference, which is
 * right for a standalone app and wrong for a widget on a partner page with a
 * fixed palette — under `auto` a light partner site gets a dark modal whenever
 * the visitor's machine happens to be dark, which is a coin-flip on someone
 * else's checkout. Partners with a committed dark look pin `'dark'`.
 */
export type OverlayColorScheme = 'light' | 'dark' | 'auto';

/** Applied when the partner doesn't pin one. See {@link OverlayColorScheme}. */
export const DEFAULT_COLOR_SCHEME: OverlayColorScheme = 'light';

export interface OverlayOptions {
  readonly url: string;
  readonly title: string;
  readonly colorScheme?: OverlayColorScheme;
  /** Fires only when the user CONFIRMS leaving. The X and ESC raise the
   *  confirmation; the backdrop is inert. */
  readonly onDismiss: () => void;
}

export interface OverlayThemeTokens {
  readonly accent?: string;
  readonly accentHot?: string;
  readonly accentGlow?: string;
}

export interface OverlayHandle {
  readonly iframe: HTMLIFrameElement;
  /**
   * Reveal the iframe and dismiss the loading animation. Idempotent.
   * Called by the SDK when `sg.session.ready` arrives so the loader
   * sits underneath the iframe until the embed page is actually
   * usable (not just when the document has loaded).
   *
   * Optionally accepts a theme — when present, applied to the
   * overlay's CSS variables so the chrome (loader, focus ring)
   * matches the iframe content's brand.
   */
  setReady(theme?: OverlayThemeTokens): void;
  /** Remove the backdrop + iframe + listeners. Idempotent. */
  destroy(): void;
}

/**
 * Build a full-screen overlay with the iframe centered (or full-bleed
 * on narrow viewports). Returns both the iframe element (for the
 * caller to listen on) and a destroy() that tears the whole thing
 * down.
 *
 * All styles are inline so the SDK works in any partner site without
 * relying on a stylesheet. We scope styles to a unique class to
 * avoid colliding with whatever the partner's page has.
 */
export function createOverlay(opts: OverlayOptions): OverlayHandle {
  ensureStylesheet();

  const backdrop = document.createElement('div');
  backdrop.className = 'playsteady-overlay-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', opts.title);
  const scheme = opts.colorScheme ?? DEFAULT_COLOR_SCHEME;
  // Every dark rule is scoped to this attribute, so the palette is one
  // attribute rather than a token-by-token override on the partner's side.
  backdrop.setAttribute('data-playsteady-theme', scheme);
  // Tells the browser which palette form controls and scrollbars inside the
  // overlay should adopt — without it a pinned-dark overlay still renders
  // light native chrome.
  backdrop.style.colorScheme = scheme === 'auto' ? 'light dark' : scheme;

  const card = document.createElement('div');
  card.className = 'playsteady-overlay-card';

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.className = 'playsteady-overlay-close';
  closeBtn.innerHTML =
    '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">' +
    '<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/>' +
    '</svg>';

  // Body wrapper that lets us stack the loader behind a transparent
  // iframe. The iframe fades in once the embed page reports ready.
  const body = document.createElement('div');
  body.className = 'playsteady-overlay-body';

  const loader = createLoader();

  const iframe = document.createElement('iframe');
  iframe.src = opts.url;
  iframe.title = opts.title;
  iframe.allow = IFRAME_ALLOW;
  iframe.className = 'playsteady-overlay-iframe';

  body.appendChild(loader);
  body.appendChild(iframe);
  // Leave-confirmation bar. Built up front and kept hidden rather than created
  // on demand: it slides in, and an element inserted and transitioned in the
  // same frame doesn't animate.
  const scrim = document.createElement('div');
  scrim.className = 'playsteady-overlay-scrim';

  const confirm = document.createElement('div');
  confirm.className = 'playsteady-overlay-confirm';
  confirm.setAttribute('role', 'alertdialog');
  confirm.setAttribute('aria-label', 'Leave without finishing?');
  confirm.setAttribute('aria-hidden', 'true');
  confirm.innerHTML =
    '<p class="playsteady-overlay-confirm-text">Leave without finishing?</p>' +
    '<div class="playsteady-overlay-confirm-actions">' +
    '<button type="button" class="playsteady-overlay-confirm-stay">Keep going</button>' +
    '<button type="button" class="playsteady-overlay-confirm-leave">Leave</button>' +
    '</div>';

  card.appendChild(closeBtn);
  card.appendChild(body);
  card.appendChild(scrim);
  card.appendChild(confirm);
  backdrop.appendChild(card);
  document.body.appendChild(backdrop);

  // Lock body scroll while the overlay is open so the underlying
  // partner page doesn't move under the modal.
  const previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  let destroyed = false;
  let isReady = false;
  const mountedAt = Date.now();
  // Hold the loader for at least this long, even if the iframe
  // reports ready sooner. On fast networks the embed page can
  // exchange + render in ~200ms, which is too short to read the
  // animation — the user sees a flicker rather than a polished
  // loading state. The build-in (0.28s delay + 1.15s drop) finishes
  // at 1.43s; holding to 1.8s means the mark always completes its
  // wobble and settles level before the iframe takes over, rather
  // than being cut mid-drop.
  const MIN_LOADER_HOLD_MS = 1800;

  const destroy = (): void => {
    if (destroyed) return;
    destroyed = true;
    document.body.style.overflow = previousBodyOverflow;
    document.removeEventListener('keydown', onKeydown);
    backdrop.remove();
  };

  const reveal = (): void => {
    if (destroyed) return;
    iframe.classList.add('playsteady-overlay-iframe--ready');
    loader.classList.add('playsteady-overlay-loader--leaving');
    // Drop the loader after the CSS transition finishes so it doesn't
    // intercept clicks while fading out.
    window.setTimeout(() => loader.remove(), 360);
  };

  const applyTheme = (theme: OverlayThemeTokens | undefined): void => {
    if (!theme) return;
    // Set CSS vars on the backdrop so they cascade into the loader,
    // close button, and any other themable bits. Empty/whitespace
    // values are skipped so the SDK's neutral defaults stay in effect
    // for tokens the page didn't resolve.
    const root = backdrop.style;
    if (theme.accent && theme.accent.trim()) {
      root.setProperty('--playsteady-sdk-accent', theme.accent.trim());
      // Glow defaults to a color-mix off the accent if not provided
      // explicitly. Browsers without color-mix() fall back to the
      // partner-side or SDK-side default.
      if (!theme.accentGlow) {
        root.setProperty(
          '--playsteady-sdk-glow',
          `color-mix(in srgb, ${theme.accent.trim()} 24%, transparent)`,
        );
      }
      // Focus ring follows the brand too — partners that want
      // something different override via their own --playsteady-sdk-focus-ring.
      root.setProperty('--playsteady-sdk-focus-ring', theme.accent.trim());
    }
    if (theme.accentGlow && theme.accentGlow.trim()) {
      root.setProperty('--playsteady-sdk-glow', theme.accentGlow.trim());
    }
  };

  const setReady = (theme?: OverlayThemeTokens): void => {
    if (isReady || destroyed) return;
    isReady = true;
    applyTheme(theme);
    const elapsed = Date.now() - mountedAt;
    const remaining = MIN_LOADER_HOLD_MS - elapsed;
    if (remaining <= 0) {
      reveal();
    } else {
      window.setTimeout(reveal, remaining);
    }
  };

  // Nothing dismisses this overlay silently. A deposit flow can be several
  // steps in — bank linked, identity submitted — and a stray click on the page
  // behind it used to throw all of that away with no way back. Backdrop clicks
  // are now inert; the X and Escape both raise the confirmation instead.
  let confirming = false;
  const setConfirming = (next: boolean): void => {
    if (destroyed || confirming === next) return;
    confirming = next;
    confirm.classList.toggle('playsteady-overlay-confirm--open', next);
    scrim.classList.toggle('playsteady-overlay-scrim--open', next);
    confirm.setAttribute('aria-hidden', next ? 'false' : 'true');
    // Move focus onto the destructive choice's SAFER neighbour, so a keyboard
    // user who hit Escape by reflex doesn't then hit Enter and leave anyway.
    if (next) (confirm.querySelector('.playsteady-overlay-confirm-stay') as HTMLElement)?.focus();
  };

  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    // Escape while confirming backs out of the confirmation, not the flow —
    // otherwise the key that asked the question also answers it.
    if (confirming) setConfirming(false);
    else setConfirming(true);
  };
  document.addEventListener('keydown', onKeydown);

  closeBtn.addEventListener('click', () => setConfirming(true));
  confirm
    .querySelector('.playsteady-overlay-confirm-stay')
    ?.addEventListener('click', () => setConfirming(false));
  confirm
    .querySelector('.playsteady-overlay-confirm-leave')
    ?.addEventListener('click', () => opts.onDismiss());

  return { iframe, setReady, destroy };
}

/**
 * Build the loading-animation DOM. Plain `innerHTML` is fine — the
 * markup is static and CSS-driven. Styles live in OVERLAY_CSS so the
 * SDK only injects one <style> tag.
 */
function createLoader(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'playsteady-overlay-loader';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('aria-label', 'Loading PlaySteady deposit');
  el.innerHTML = LOADER_SVG;
  return el;
}

/**
 * PlaySteady's balance-beam mark: a beam seesawing on a fulcrum, forever
 * finding its level ("steady"). Geometry is a copy of `BrandLoader` in
 * `packages/ui` (and `BrandMark variant="glyph"`, and the landing page's
 * `LogoGlyph`) — the SDK ships as a self-contained zero-dependency bundle for
 * partner sites, so it cannot import the design-system package and the paths
 * are duplicated here on purpose. **If the mark ever changes, update all four.**
 *
 * Colors come from `--playsteady-loader-*`, which resolve off the public
 * `--playsteady-sdk-*` theming hooks (see OVERLAY_CSS).
 */
const LOADER_SVG = `
<div class="playsteady-loader__mark" aria-hidden="true">
  <svg class="playsteady-loader__svg" viewBox="0 0 120 130" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path class="playsteady-loader__fulcrum" d="M60 52 L92 116 L28 116 Z" />
    <rect class="playsteady-loader__beam" x="6" y="30" width="108" height="17" rx="5" />
  </svg>
</div>
`;

/** Resolve a CSS selector OR HTMLElement to the actual element. */
export function resolveContainer(target: string | HTMLElement): HTMLElement {
  if (typeof target === 'string') {
    const el = document.querySelector(target);
    if (!el) {
      throw new Error(`@playsteady/sdk-client: container "${target}" not found in document`);
    }
    if (!(el instanceof HTMLElement)) {
      throw new Error(`@playsteady/sdk-client: container "${target}" is not an HTMLElement`);
    }
    return el;
  }
  return target;
}

/**
 * Derive the origin from a launch URL. The iframe will postMessage
 * from this origin; we use it to filter incoming messages so we
 * don't react to anything other windows on the page might send.
 */
export function originFromUrl(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    throw new Error(`@playsteady/sdk-client: launchUrl is not a valid URL: ${url}`);
  }
}

// ─── overlay styles ────────────────────────────────────────────────────────

const STYLESHEET_ID = 'playsteady-sdk-overlay-styles';

function ensureStylesheet(): void {
  if (document.getElementById(STYLESHEET_ID)) return;
  const style = document.createElement('style');
  style.id = STYLESHEET_ID;
  style.textContent = OVERLAY_CSS;
  document.head.appendChild(style);
}

const OVERLAY_CSS = `
.playsteady-overlay-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483600;
  background: var(--playsteady-sdk-backdrop, rgba(8, 10, 14, 0.62));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: playsteady-overlay-fade 160ms ease-out;
  -webkit-tap-highlight-color: transparent;
}
/*
 * Blur the partner's page behind the card, where the browser can do it.
 *
 * Guarded rather than unconditional because a browser without backdrop-filter
 * silently drops the declaration — so the scrim has to carry the separation on
 * its own there, at the heavier 0.62 alpha. Inside the guard the blur is doing
 * that work, and the same alpha would read as murky.
 *
 * Safari shipped the prefixed form years before the plain one, so both are
 * tested; a partner override of --playsteady-sdk-backdrop still wins in
 * either branch, which is why the var is repeated instead of the block setting
 * a bare color.
 */
@supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
  .playsteady-overlay-backdrop {
    background: var(--playsteady-sdk-backdrop, rgba(8, 10, 14, 0.45));
    -webkit-backdrop-filter: blur(var(--playsteady-sdk-backdrop-blur, 6px));
    backdrop-filter: blur(var(--playsteady-sdk-backdrop-blur, 6px));
  }
}
.playsteady-overlay-card {
  position: relative;
  width: 100%;
  max-width: 480px;
  height: min(720px, calc(100vh - 48px));
  background: var(--playsteady-sdk-card-bg, #ffffff);
  border-radius: var(--playsteady-sdk-card-radius, 14px);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.32), 0 4px 12px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: playsteady-overlay-pop 220ms cubic-bezier(0.16, 1, 0.3, 1);
}
.playsteady-overlay-body {
  position: relative;
  flex: 1;
  display: block;
  background: inherit;
}
.playsteady-overlay-iframe {
  border: 0;
  width: 100%;
  height: 100%;
  display: block;
  opacity: 0;
  transition: opacity 240ms ease;
}
.playsteady-overlay-iframe--ready {
  opacity: 1;
}
.playsteady-overlay-confirm {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 16px;
  background: var(--playsteady-sdk-confirm-bg, #ffffff);
  border-top: 1px solid var(--playsteady-sdk-confirm-border, rgba(20, 22, 26, 0.1));
  box-shadow: 0 -8px 24px rgba(20, 22, 26, 0.12);
  /* Off-screen and inert until raised. visibility (not display) so the slide
     can transition, and so the buttons aren't tabbable while hidden. */
  transform: translateY(115%);
  visibility: hidden;
  opacity: 0;
  transition:
    transform 0.34s cubic-bezier(0.22, 1.4, 0.36, 1),
    opacity 0.18s ease,
    visibility 0s linear 0.34s;
}
.playsteady-overlay-confirm--open {
  /* Overshoots slightly and settles. A bar that merely appears at the bottom
     of a busy checkout gets missed; the small bounce is what makes the eye
     follow it, which is the entire job of this element. */
  transform: translateY(0);
  visibility: visible;
  opacity: 1;
  transition:
    transform 0.34s cubic-bezier(0.22, 1.4, 0.36, 1),
    opacity 0.18s ease,
    visibility 0s;
}
/* Dim the flow behind the bar while it's up, so the question is unmistakably
   the thing being asked rather than one more row of a form. */
.playsteady-overlay-scrim {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: rgba(20, 22, 26, 0.42);
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.2s ease,
    visibility 0s linear 0.2s;
}
.playsteady-overlay-scrim--open {
  opacity: 1;
  visibility: visible;
  transition:
    opacity 0.2s ease,
    visibility 0s;
}
@media (prefers-reduced-motion: reduce) {
  .playsteady-overlay-confirm,
  .playsteady-overlay-confirm--open {
    /* No travel and no overshoot — but keep the fade, so the bar still
       announces itself rather than snapping into place. */
    transform: none;
    transition:
      opacity 0.18s ease,
      visibility 0s;
  }
}
.playsteady-overlay-confirm-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--playsteady-sdk-confirm-fg, #14161a);
}
.playsteady-overlay-confirm-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.playsteady-overlay-confirm-actions button {
  border-radius: 8px;
  border: 0;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.playsteady-overlay-confirm-actions button:focus-visible {
  outline: 2px solid var(--playsteady-sdk-focus-ring, #14161a);
  outline-offset: 2px;
}
/* Staying carries the solid fill; leaving is outlined. Both are unmistakably
   buttons — a bare text link at the edge of a modal reads as decoration, and a
   confirmation whose exit is hard to find isn't a confirmation, it's a trap. */
.playsteady-overlay-confirm-stay {
  background: var(--playsteady-sdk-accent, #14161a);
  color: var(--playsteady-sdk-confirm-stay-fg, #ffffff);
}
.playsteady-overlay-confirm-leave {
  background: var(--playsteady-sdk-confirm-leave-bg, transparent);
  color: var(--playsteady-sdk-confirm-leave-fg, #14161a);
  box-shadow: inset 0 0 0 1.5px var(--playsteady-sdk-confirm-leave-border, rgba(20, 22, 26, 0.28));
}
.playsteady-overlay-confirm-leave:hover {
  background: var(--playsteady-sdk-confirm-leave-bg-hover, rgba(20, 22, 26, 0.06));
}
@media (prefers-color-scheme: dark) {
  [data-playsteady-theme='auto'] .playsteady-overlay-confirm {
    background: var(--playsteady-sdk-confirm-bg, #14161a);
    border-top-color: var(--playsteady-sdk-confirm-border, rgba(255, 255, 255, 0.12));
  }
  [data-playsteady-theme='auto'] .playsteady-overlay-confirm-text {
    color: var(--playsteady-sdk-confirm-fg, #f6f7f9);
  }
  [data-playsteady-theme='auto'] .playsteady-overlay-confirm-leave {
    color: var(--playsteady-sdk-confirm-leave-fg, #f6f7f9);
    box-shadow: inset 0 0 0 1.5px
      var(--playsteady-sdk-confirm-leave-border, rgba(246, 247, 249, 0.32));
  }
  [data-playsteady-theme='auto'] .playsteady-overlay-confirm-leave:hover {
    background: var(--playsteady-sdk-confirm-leave-bg-hover, rgba(246, 247, 249, 0.1));
  }
}
[data-playsteady-theme='dark'] .playsteady-overlay-confirm {
  background: var(--playsteady-sdk-confirm-bg, #14161a);
  border-top-color: var(--playsteady-sdk-confirm-border, rgba(255, 255, 255, 0.12));
  }
[data-playsteady-theme='dark'] .playsteady-overlay-confirm-text {
  color: var(--playsteady-sdk-confirm-fg, #f6f7f9);
  }
[data-playsteady-theme='dark'] .playsteady-overlay-confirm-leave {
  color: var(--playsteady-sdk-confirm-leave-fg, #f6f7f9);
  box-shadow: inset 0 0 0 1.5px
    var(--playsteady-sdk-confirm-leave-border, rgba(246, 247, 249, 0.32));
  }
[data-playsteady-theme='dark'] .playsteady-overlay-confirm-leave:hover {
  background: var(--playsteady-sdk-confirm-leave-bg-hover, rgba(246, 247, 249, 0.1));
  }
.playsteady-overlay-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 0;
  background: var(--playsteady-sdk-close-bg, rgba(20, 22, 26, 0.06));
  color: var(--playsteady-sdk-close-fg, #14161a);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 120ms ease;
}
.playsteady-overlay-close:hover {
  background: var(--playsteady-sdk-close-bg-hover, rgba(20, 22, 26, 0.12));
}
.playsteady-overlay-close:focus-visible {
  /* Brand steel-blue (--color-brand-400), matching the loader accent. */
  outline: 2px solid var(--playsteady-sdk-focus-ring, #6b9ab8);
  outline-offset: 2px;
}
@media (max-width: 640px) {
  .playsteady-overlay-backdrop {
    padding: 0;
  }
  .playsteady-overlay-card {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
}
@media (prefers-color-scheme: dark) {
  [data-playsteady-theme='auto'] .playsteady-overlay-card {
    background: var(--playsteady-sdk-card-bg, #14161a);
  }
  [data-playsteady-theme='auto'] .playsteady-overlay-close {
    background: var(--playsteady-sdk-close-bg, rgba(255, 255, 255, 0.08));
    color: var(--playsteady-sdk-close-fg, #ecedea);
  }
  [data-playsteady-theme='auto'] .playsteady-overlay-close:hover {
    background: var(--playsteady-sdk-close-bg-hover, rgba(255, 255, 255, 0.16));
  }
}
[data-playsteady-theme='dark'] .playsteady-overlay-card {
  background: var(--playsteady-sdk-card-bg, #14161a);
  }
[data-playsteady-theme='dark'] .playsteady-overlay-close {
  background: var(--playsteady-sdk-close-bg, rgba(255, 255, 255, 0.08));
  color: var(--playsteady-sdk-close-fg, #ecedea);
  }
[data-playsteady-theme='dark'] .playsteady-overlay-close:hover {
  background: var(--playsteady-sdk-close-bg-hover, rgba(255, 255, 255, 0.16));
  }
@keyframes playsteady-overlay-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes playsteady-overlay-pop {
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .playsteady-overlay-backdrop,
  .playsteady-overlay-card { animation: none; }
  .playsteady-overlay-iframe { transition: none; }
}


/* ─── Loader animation ─────────────────────────────────────────── */
/* Every CSS variable below is a public theming hook. Partners
   override them via :root { --playsteady-sdk-accent: ... } anywhere in
   their cascade; the var() fallback form means the SDK ships with
   PlaySteady's brand defaults but yields to any partner-set value. */
.playsteady-overlay-loader {
  /* Defaults are PlaySteady's brand tokens, mirrored from packages/ui
     theme.css: beam --color-brand-400, fulcrum --color-text-brand-primary
     (brand-900 light / gray-50 dark). Partners override via the
     --playsteady-sdk-* hooks for a different brand-side accent. */
  --playsteady-loader-primary: var(--playsteady-sdk-primary, #1b2a41);
  --playsteady-loader-accent: var(--playsteady-sdk-accent, #6b9ab8);
  --playsteady-loader-glow: var(--playsteady-sdk-glow, rgba(107, 154, 184, 0.24));
  --playsteady-loader-size: 112px;

  position: absolute;
  inset: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  pointer-events: none;
  color: var(--playsteady-loader-primary);
  opacity: 1;
  transition: opacity 320ms ease;
}
.playsteady-overlay-loader--leaving { opacity: 0; }

@media (prefers-color-scheme: dark) {
  [data-playsteady-theme='auto'] .playsteady-overlay-loader {
    --playsteady-loader-primary: var(--playsteady-sdk-primary, #fafafa);
    --playsteady-loader-accent: var(--playsteady-sdk-accent, #6b9ab8);
    --playsteady-loader-glow: var(--playsteady-sdk-glow, rgba(107, 154, 184, 0.28));
  }
}
[data-playsteady-theme='dark'] .playsteady-overlay-loader {
  --playsteady-loader-primary: var(--playsteady-sdk-primary, #fafafa);
  --playsteady-loader-accent: var(--playsteady-sdk-accent, #6b9ab8);
  --playsteady-loader-glow: var(--playsteady-sdk-glow, rgba(107, 154, 184, 0.28));
  }

.playsteady-loader__mark {
  position: relative;
  width: var(--playsteady-loader-size);
  height: calc(var(--playsteady-loader-size) * (130 / 120));
  display: grid;
  place-items: center;
}
.playsteady-loader__svg { width: 100%; height: 100%; overflow: visible; }

/* Build-in, then loop — mirrors BrandLoader in packages/ui. The fulcrum
   rises, the beam drops onto it and wobbles down to level, then the rock
   takes over and seesaws forever. The handoff is seamless because the rock
   starts and ends at rotate(0deg) (the state the drop settles on) and its
   1.43s delay is the drop's 0.28s delay + 1.15s duration. */
.playsteady-loader__fulcrum {
  fill: var(--playsteady-loader-primary);
  transform-box: view-box;
  transform-origin: 60px 116px;
  animation: playsteady-fulcrum-rise 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
}
.playsteady-loader__beam {
  fill: var(--playsteady-loader-accent);
  filter: drop-shadow(0 0 8px var(--playsteady-loader-glow));
  transform-box: view-box;
  transform-origin: 60px 47px;
  animation:
    playsteady-beam-drop 1.15s ease-out 0.28s both,
    playsteady-beam-rock 1.8s cubic-bezier(0.45, 0, 0.55, 1) 1.43s infinite;
}

@keyframes playsteady-fulcrum-rise {
  from { transform: translateY(14px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}
@keyframes playsteady-beam-drop {
  0%   { transform: translateY(-30px) rotate(0deg); opacity: 0; animation-timing-function: cubic-bezier(0.45, 0, 0.9, 0.45); }
  40%  { opacity: 1; }
  52%  { transform: translateY(0) rotate(0deg); animation-timing-function: cubic-bezier(0.4, 0, 0.3, 1); }
  66%  { transform: translateY(0) rotate(6.5deg); animation-timing-function: ease-in-out; }
  80%  { transform: translateY(0) rotate(-4deg); animation-timing-function: ease-in-out; }
  91%  { transform: translateY(0) rotate(2deg); animation-timing-function: ease-in-out; }
  100% { transform: translateY(0) rotate(0deg); opacity: 1; }
}
@keyframes playsteady-beam-rock {
  0%   { transform: rotate(0deg); }
  25%  { transform: rotate(9deg); }
  75%  { transform: rotate(-9deg); }
  100% { transform: rotate(0deg); }
}

@media (prefers-reduced-motion: reduce) {
  .playsteady-loader__beam,
  .playsteady-loader__fulcrum {
    animation: none;
    opacity: 1;
    transform: none;
  }
}
`;
